import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useApi } from '../hooks/useApi';
import { Layout } from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { formatDateTime } from '../lib/utils';
import { 
  Mail, 
  Eye, 
  MousePointerClick,
  TrendingUp,
  Users,
  BarChart3,
  CheckCircle,
  Clock,
  Loader2,
  ArrowUpRight
} from 'lucide-react';

export default function EmailAnalytics() {
  const { user } = useAuth();
  const { get, loading } = useApi();
  
  const [analytics, setAnalytics] = useState(null);
  const [selectedDesign, setSelectedDesign] = useState(null);
  const [designAnalytics, setDesignAnalytics] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  const fetchAnalytics = async () => {
    try {
      const data = await get('/email-analytics');
      setAnalytics(data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    }
  };

  useEffect(() => {
    fetchAnalytics();
    // Refresh every 30 seconds
    const interval = setInterval(fetchAnalytics, 30000);
    return () => clearInterval(interval);
  }, []);

  const openDesignDetails = async (designId) => {
    try {
      const data = await get(`/email-analytics/${designId}`);
      setDesignAnalytics(data);
      setDetailDialogOpen(true);
    } catch (error) {
      console.error('Failed to fetch design analytics:', error);
    }
  };

  const getStatusBadge = (send) => {
    if (send.clicked_at) {
      return <Badge className="bg-emerald-500 text-white rounded-sm text-xs">Clicked</Badge>;
    } else if (send.opened_at) {
      return <Badge className="bg-blue-500 text-white rounded-sm text-xs">Opened</Badge>;
    } else {
      return <Badge variant="secondary" className="rounded-sm text-xs">Sent</Badge>;
    }
  };

  if (!analytics) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="email-analytics-page">
        {/* Header */}
        <div>
          <h1 className="text-3xl sm:text-4xl font-semibold mb-2">Email Analytics</h1>
          <p className="text-muted-foreground">
            Track opens, clicks, and engagement for your premium emails
          </p>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card className="rounded-sm border" data-testid="stat-total-sent">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <Mail className="h-5 w-5 text-muted-foreground" />
              </div>
              <p className="text-2xl font-semibold mt-2">{analytics.summary.total_sent}</p>
              <p className="text-xs text-muted-foreground">Emails Sent</p>
            </CardContent>
          </Card>

          <Card className="rounded-sm border" data-testid="stat-total-opened">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <Eye className="h-5 w-5 text-blue-500" />
              </div>
              <p className="text-2xl font-semibold mt-2">{analytics.summary.total_opened}</p>
              <p className="text-xs text-muted-foreground">Opened</p>
            </CardContent>
          </Card>

          <Card className="rounded-sm border" data-testid="stat-click-rate">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <MousePointerClick className="h-5 w-5 text-purple-500" />
              </div>
              <p className="text-2xl font-semibold mt-2">{analytics.summary.click_rate}%</p>
              <p className="text-xs text-muted-foreground">Click Rate <span className="text-emerald-600 font-medium">· primary</span></p>
            </CardContent>
          </Card>

          <Card className="rounded-sm border" data-testid="stat-verified-opens">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <Eye className="h-5 w-5 text-emerald-500" />
              </div>
              <p className="text-2xl font-semibold mt-2">{analytics.summary.verified_open_rate ?? 0}%</p>
              <p className="text-xs text-muted-foreground" title="Opens that survived Gmail's image proxy filter (non-prefetch UA + >2min after send). Closer to true human engagement than raw open rate.">Verified Opens</p>
            </CardContent>
          </Card>

          <Card className="rounded-sm border" data-testid="stat-open-rate">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <TrendingUp className="h-5 w-5 text-muted-foreground" />
              </div>
              <p className="text-2xl font-semibold mt-2 text-muted-foreground">{analytics.summary.open_rate}%</p>
              <p className="text-xs text-muted-foreground" title="Raw open rate. Inflated by Gmail/Outlook prefetching the tracking pixel on inbox arrival (before any human opens). Use Click Rate or Verified Opens for real engagement.">Open Rate <span className="text-amber-600">⚠ prefetch-inflated</span></p>
            </CardContent>
          </Card>
        </div>

        {analytics.summary.excluded_test_sends > 0 && (
          <p className="text-xs text-muted-foreground -mt-2 mb-2" data-testid="excluded-test-banner">
            Excluding {analytics.summary.excluded_test_sends} test send{analytics.summary.excluded_test_sends === 1 ? '' : 's'} to your own inboxes. Add <code>?exclude_tests=false</code> in the URL to include them.
          </p>
        )}

        <Tabs defaultValue="by-email" className="space-y-4">
          <TabsList className="rounded-sm">
            <TabsTrigger value="by-email" className="rounded-sm">By Email Design</TabsTrigger>
            <TabsTrigger value="recent" className="rounded-sm">Recent Sends</TabsTrigger>
          </TabsList>

          {/* By Email Design */}
          <TabsContent value="by-email">
            <Card className="rounded-sm border">
              <CardHeader>
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <BarChart3 className="h-4 w-4" />
                  Performance by Email Design
                </CardTitle>
              </CardHeader>
              <CardContent>
                {analytics.by_design.length === 0 ? (
                  <p className="text-center py-8 text-muted-foreground">No emails sent yet</p>
                ) : (
                  <div className="space-y-4">
                    {analytics.by_design.map((design) => {
                      const openRate = design.sent > 0 ? (design.opened / design.sent * 100).toFixed(1) : 0;
                      const clickRate = design.sent > 0 ? (design.clicked / design.sent * 100).toFixed(1) : 0;
                      
                      return (
                        <div 
                          key={design.design_id} 
                          className="p-4 border rounded-sm hover:bg-muted/30 cursor-pointer transition-colors"
                          onClick={() => openDesignDetails(design.design_id)}
                          data-testid={`design-stat-${design.design_id}`}
                        >
                          <div className="flex items-center justify-between mb-3">
                            <div>
                              <h3 className="font-medium">{design.design_name || 'Untitled'}</h3>
                              <p className="text-xs text-muted-foreground">{design.subject}</p>
                            </div>
                            <ArrowUpRight className="h-4 w-4 text-muted-foreground" />
                          </div>
                          
                          <div className="grid grid-cols-4 gap-4 text-center">
                            <div>
                              <p className="text-lg font-semibold">{design.sent}</p>
                              <p className="text-xs text-muted-foreground">Sent</p>
                            </div>
                            <div>
                              <p className="text-lg font-semibold text-blue-500">{design.opened}</p>
                              <p className="text-xs text-muted-foreground">Opened</p>
                            </div>
                            <div>
                              <p className="text-lg font-semibold text-emerald-500">{design.clicked}</p>
                              <p className="text-xs text-muted-foreground">Clicked</p>
                            </div>
                            <div>
                              <p className="text-lg font-semibold">{openRate}%</p>
                              <p className="text-xs text-muted-foreground">Open Rate</p>
                            </div>
                          </div>
                          
                          {/* Progress bars */}
                          <div className="mt-3 space-y-2">
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-muted-foreground w-16">Opens</span>
                              <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-blue-500 rounded-full transition-all"
                                  style={{ width: `${openRate}%` }}
                                />
                              </div>
                              <span className="text-xs text-muted-foreground w-12 text-right">{openRate}%</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-muted-foreground w-16">Clicks</span>
                              <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-emerald-500 rounded-full transition-all"
                                  style={{ width: `${clickRate}%` }}
                                />
                              </div>
                              <span className="text-xs text-muted-foreground w-12 text-right">{clickRate}%</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Recent Sends */}
          <TabsContent value="recent">
            <Card className="rounded-sm border">
              <CardHeader>
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Recent Email Activity
                </CardTitle>
              </CardHeader>
              <CardContent>
                {analytics.recent_sends.length === 0 ? (
                  <p className="text-center py-8 text-muted-foreground">No emails sent yet</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-3 px-2 text-xs font-medium text-muted-foreground">Recipient</th>
                          <th className="text-left py-3 px-2 text-xs font-medium text-muted-foreground">Subject</th>
                          <th className="text-left py-3 px-2 text-xs font-medium text-muted-foreground">Sent</th>
                          <th className="text-left py-3 px-2 text-xs font-medium text-muted-foreground">Status</th>
                          <th className="text-left py-3 px-2 text-xs font-medium text-muted-foreground">Opened</th>
                          <th className="text-left py-3 px-2 text-xs font-medium text-muted-foreground">Clicked</th>
                        </tr>
                      </thead>
                      <tbody>
                        {analytics.recent_sends.map((send) => (
                          <tr key={send.id || send.tracking_id} className="border-b last:border-0 hover:bg-muted/30">
                            <td className="py-3 px-2">
                              <div>
                                <p className="font-medium text-sm">{send.recipient_name || '—'}</p>
                                <p className="text-xs text-muted-foreground">{send.recipient_email}</p>
                              </div>
                            </td>
                            <td className="py-3 px-2 text-sm max-w-[200px] truncate">{send.subject}</td>
                            <td className="py-3 px-2 text-xs text-muted-foreground">{formatDateTime(send.sent_at)}</td>
                            <td className="py-3 px-2">{getStatusBadge(send)}</td>
                            <td className="py-3 px-2 text-xs">
                              {send.opened_at ? (
                                <span className="text-blue-500">{formatDateTime(send.opened_at)}</span>
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </td>
                            <td className="py-3 px-2 text-xs">
                              {send.clicked_at ? (
                                <span className="text-emerald-500">{formatDateTime(send.clicked_at)}</span>
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Design Detail Dialog */}
        <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="design-detail-dialog">
            <DialogHeader>
              <DialogTitle className="text-xl font-semibold">Email Performance Details</DialogTitle>
            </DialogHeader>
            
            {designAnalytics && (
              <div className="space-y-6 mt-4">
                {/* Summary */}
                <div className="grid grid-cols-4 gap-4">
                  <div className="text-center p-3 bg-muted/30 rounded-sm">
                    <p className="text-2xl font-semibold">{designAnalytics.total_sent}</p>
                    <p className="text-xs text-muted-foreground">Sent</p>
                  </div>
                  <div className="text-center p-3 bg-blue-500/10 rounded-sm">
                    <p className="text-2xl font-semibold text-blue-500">{designAnalytics.total_opened}</p>
                    <p className="text-xs text-muted-foreground">Opened</p>
                  </div>
                  <div className="text-center p-3 bg-emerald-500/10 rounded-sm">
                    <p className="text-2xl font-semibold text-emerald-500">{designAnalytics.total_clicked}</p>
                    <p className="text-xs text-muted-foreground">Clicked</p>
                  </div>
                  <div className="text-center p-3 bg-purple-500/10 rounded-sm">
                    <p className="text-2xl font-semibold text-purple-500">{designAnalytics.click_rate}%</p>
                    <p className="text-xs text-muted-foreground">CTR</p>
                  </div>
                </div>

                {/* Recipients List */}
                <div>
                  <h3 className="font-medium mb-3">Recipients</h3>
                  <div className="space-y-2 max-h-[300px] overflow-y-auto">
                    {designAnalytics.recipients.map((recipient) => (
                      <div 
                        key={recipient.tracking_id || recipient.id} 
                        className="flex items-center justify-between p-3 border rounded-sm"
                      >
                        <div>
                          <p className="font-medium text-sm">{recipient.recipient_name || recipient.recipient_email}</p>
                          <p className="text-xs text-muted-foreground">{recipient.recipient_email}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          {recipient.opened_at && (
                            <div className="flex items-center gap-1 text-blue-500">
                              <Eye className="h-3 w-3" />
                              <span className="text-xs">{recipient.open_count || 1}x</span>
                            </div>
                          )}
                          {recipient.clicked_at && (
                            <div className="flex items-center gap-1 text-emerald-500">
                              <MousePointerClick className="h-3 w-3" />
                              <span className="text-xs">{recipient.click_count || 1}x</span>
                            </div>
                          )}
                          {getStatusBadge(recipient)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
